import { escapeHtml } from '../lib/escape';
import { isChoice, isPhoto, notifyContent, notifyStructure, state, subscribeStructure } from '../state';
import type { Target } from '../types';

interface Elements {
  container: HTMLElement;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}

let targetSeq = 0;
function nextTargetId(): string {
  return `target-new-${Date.now().toString(36)}-${(targetSeq++).toString(36)}`;
}

function questionsUsingTarget(targetName: string): string[] {
  const out: string[] = [];
  for (const q of state.questions) {
    let uses = false;
    if (isChoice(q)) {
      for (const opt of q.options) {
        for (const p of opt.prompts) {
          if (p.alternatives.some((a) => a.target === targetName)) {
            uses = true;
            break;
          }
        }
        if (uses) break;
      }
    } else if (isPhoto(q)) {
      for (const p of q.prompts) {
        if (p.alternatives.some((a) => a.target === targetName)) {
          uses = true;
          break;
        }
      }
    }
    if (uses) out.push(q.id);
  }
  return out;
}

function effectivePriority(target: Target): string[] {
  const using = questionsUsingTarget(target.name);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const qid of target.priority) {
    if (using.includes(qid) && !seen.has(qid)) {
      seen.add(qid);
      out.push(qid);
    }
  }
  for (const qid of using) {
    if (!seen.has(qid)) {
      seen.add(qid);
      out.push(qid);
    }
  }
  return out;
}

export function mountTargetsSection(opts: Elements): void {
  const { container, onCollapsedChange } = opts;
  let collapsed = opts.collapsed;
  const expandedPriority = new Set<string>();

  const render = (): void => {
    container.classList.add('prompt-section', 'prompt-section-collapsible', 'targets-section');
    container.classList.toggle('collapsed', collapsed);

    for (const id of Array.from(expandedPriority)) {
      if (!state.targets.some((t) => t.id === id)) expandedPriority.delete(id);
    }

    const listHtml = state.targets.length === 0
      ? `<p class="targets-empty">No targets yet. Add one to reference it in answer prompts.</p>`
      : state.targets
          .map((t) => renderTargetRow(t, expandedPriority.has(t.id)))
          .join('');

    container.innerHTML = `
      <header class="prompt-section-header" data-action="toggle">
        <span class="prompt-section-label">Targets</span>
        <span class="targets-count">${state.targets.length}</span>
        <span class="prompt-section-toggle"></span>
      </header>
      <div class="prompt-section-body targets-body">
        <div class="target-list" id="target-list">${listHtml}</div>
        <button type="button" class="target-add-btn" data-action="add">+ Add target</button>
      </div>
    `;
  };

  const onClick = (e: Event): void => {
    if (!(e.target instanceof Element)) return;

    const toggle = e.target.closest<HTMLElement>('[data-action="toggle"]');
    if (toggle && container.contains(toggle)) {
      collapsed = !collapsed;
      container.classList.toggle('collapsed', collapsed);
      onCollapsedChange(collapsed);
      return;
    }

    const actionEl = e.target.closest<HTMLElement>('[data-action]');
    if (!actionEl) return;
    const action = actionEl.dataset.action;

    if (action === 'add') {
      state.targets.push({
        id: nextTargetId(),
        name: `target ${state.targets.length + 1}`,
        priority: [],
      });
      render();
      notifyContent();
      return;
    }

    if (action === 'delete') {
      const row = actionEl.closest<HTMLElement>('.target-row');
      const id = row?.dataset.targetId;
      if (!id) return;
      state.targets = state.targets.filter((t) => t.id !== id);
      for (const area of state.areas) {
        area.targets = area.targets.filter((at) => at.targetId !== id);
      }
      render();
      notifyStructure();
      return;
    }

    if (action === 'toggle-priority') {
      const row = actionEl.closest<HTMLElement>('.target-row');
      const id = row?.dataset.targetId;
      if (!id) return;
      if (expandedPriority.has(id)) expandedPriority.delete(id);
      else expandedPriority.add(id);
      render();
    }
  };

  const onInput = (e: Event): void => {
    if (!(e.target instanceof HTMLInputElement)) return;
    if (e.target.dataset.action !== 'rename') return;
    const row = e.target.closest<HTMLElement>('.target-row');
    const id = row?.dataset.targetId;
    if (!id) return;
    const target = state.targets.find((t) => t.id === id);
    if (!target) return;
    target.name = e.target.value;
    notifyContent();
  };

  const onDragStart = (e: DragEvent): void => {
    if (!(e.target instanceof HTMLElement)) return;

    const prioRow = e.target.closest<HTMLElement>('.target-priority-row');
    if (prioRow && container.contains(prioRow)) {
      if (e.target instanceof HTMLInputElement) {
        e.preventDefault();
        return;
      }
      const targetRow = prioRow.closest<HTMLElement>('.target-row');
      const targetId = targetRow?.dataset.targetId ?? '';
      const qid = prioRow.dataset.qid ?? '';
      e.dataTransfer?.setData('text/plain', `target-priority:${targetId}:${qid}`);
      if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
      prioRow.classList.add('is-dragging');
      return;
    }

    const row = e.target.closest<HTMLElement>('.target-row');
    if (!row || !container.contains(row)) return;
    if (e.target instanceof HTMLInputElement) {
      e.preventDefault();
      return;
    }
    e.dataTransfer?.setData('text/plain', `target:${row.dataset.targetId}`);
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
    row.classList.add('is-dragging');
  };

  const onDragOver = (e: DragEvent): void => {
    if (!(e.target instanceof HTMLElement)) return;
    if (!hasTargetDrag(e)) return;

    const prioRow = e.target.closest<HTMLElement>('.target-priority-row');
    if (prioRow && container.contains(prioRow)) {
      e.preventDefault();
      e.stopPropagation();
      const rect = prioRow.getBoundingClientRect();
      const above = e.clientY - rect.top < rect.height / 2;
      prioRow.classList.toggle('drop-above', above);
      prioRow.classList.toggle('drop-below', !above);
      return;
    }

    const row = e.target.closest<HTMLElement>('.target-row');
    if (!row || !container.contains(row)) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = row.getBoundingClientRect();
    const above = e.clientY - rect.top < rect.height / 2;
    row.classList.toggle('drop-above', above);
    row.classList.toggle('drop-below', !above);
  };

  const onDragLeave = (e: DragEvent): void => {
    if (!(e.target instanceof HTMLElement)) return;
    const prioRow = e.target.closest<HTMLElement>('.target-priority-row');
    if (prioRow) prioRow.classList.remove('drop-above', 'drop-below');
    const row = e.target.closest<HTMLElement>('.target-row');
    if (row) row.classList.remove('drop-above', 'drop-below');
  };

  const onDrop = (e: DragEvent): void => {
    if (!(e.target instanceof HTMLElement)) return;
    const data = e.dataTransfer?.getData('text/plain') ?? '';

    if (data.startsWith('target-priority:')) {
      const prioRow = e.target.closest<HTMLElement>('.target-priority-row');
      if (!prioRow || !container.contains(prioRow)) return;
      e.preventDefault();
      e.stopPropagation();
      const rest = data.slice('target-priority:'.length);
      const sep = rest.indexOf(':');
      if (sep < 0) return;
      const targetId = rest.slice(0, sep);
      const fromQid = rest.slice(sep + 1);
      const dropRow = prioRow.closest<HTMLElement>('.target-row');
      if (dropRow?.dataset.targetId !== targetId) return;
      const toQid = prioRow.dataset.qid ?? '';
      const above = prioRow.classList.contains('drop-above');
      prioRow.classList.remove('drop-above', 'drop-below');
      reorderPriority(targetId, fromQid, toQid, above);
      render();
      notifyContent();
      return;
    }

    if (data.startsWith('target:')) {
      const row = e.target.closest<HTMLElement>('.target-row');
      if (!row || !container.contains(row)) return;
      e.preventDefault();
      e.stopPropagation();
      const fromId = data.slice('target:'.length);
      const toId = row.dataset.targetId ?? '';
      const above = row.classList.contains('drop-above');
      row.classList.remove('drop-above', 'drop-below');
      reorder(fromId, toId, above);
      render();
      notifyContent();
    }
  };

  const onDragEnd = (): void => {
    container.querySelectorAll('.target-row, .target-priority-row').forEach((r) => {
      r.classList.remove('is-dragging', 'drop-above', 'drop-below');
    });
  };

  container.addEventListener('click', onClick);
  container.addEventListener('input', onInput);
  container.addEventListener('dragstart', onDragStart);
  container.addEventListener('dragover', onDragOver);
  container.addEventListener('dragleave', onDragLeave);
  container.addEventListener('drop', onDrop);
  container.addEventListener('dragend', onDragEnd);

  subscribeStructure(render);
  render();
}

function renderTargetRow(t: Target, expanded: boolean): string {
  const using = questionsUsingTarget(t.name);
  const hasConflict = using.length >= 2;
  const scaleIcon = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v18"/><path d="M7 21h10"/><path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2"/><path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/></svg>`;
  const priorityBtn = hasConflict
    ? `<button type="button" class="target-priority-toggle${expanded ? ' expanded' : ''}" data-action="toggle-priority" aria-label="Priority order" title="Priority order">${scaleIcon}</button>`
    : '';
  const priorityHtml = hasConflict && expanded
    ? renderPriorityEditor(t)
    : '';

  return `
    <div class="target-row${expanded ? ' expanded' : ''}" data-target-id="${t.id}" draggable="true">
      <div class="target-row-main">
        <button type="button" class="target-handle" title="Drag to reorder" tabindex="-1">⋮⋮</button>
        <input
          class="target-name-input"
          type="text"
          value="${escapeHtml(t.name)}"
          data-action="rename"
          placeholder="Target name"
        />
        ${priorityBtn}
        <button type="button" class="target-delete-btn" data-action="delete" title="Delete target">×</button>
      </div>
      ${priorityHtml}
    </div>
  `;
}

function renderPriorityEditor(t: Target): string {
  const ordered = effectivePriority(t);
  const rows = ordered
    .map(
      (qid) => `
      <div class="target-priority-row" data-qid="${escapeHtml(qid)}" draggable="true">
        <span class="target-priority-handle">⋮⋮</span>
        <span class="target-priority-qid">${escapeHtml(qid)}</span>
      </div>
    `,
    )
    .join('');
  return `
    <div class="target-priority-body">
      <div class="target-priority-label">Priority (highest first):</div>
      <div class="target-priority-list">${rows}</div>
    </div>
  `;
}

function hasTargetDrag(e: DragEvent): boolean {
  const types = e.dataTransfer?.types;
  if (!types) return false;
  return Array.from(types).includes('text/plain');
}

function reorder(fromId: string, toId: string, above: boolean): void {
  if (fromId === toId) return;
  const fromIdx = state.targets.findIndex((t) => t.id === fromId);
  if (fromIdx < 0) return;
  const [moving] = state.targets.splice(fromIdx, 1);
  let toIdx = state.targets.findIndex((t) => t.id === toId);
  if (toIdx < 0) {
    state.targets.splice(fromIdx, 0, moving);
    return;
  }
  if (!above) toIdx += 1;
  state.targets.splice(toIdx, 0, moving);
}

function reorderPriority(targetId: string, fromQid: string, toQid: string, above: boolean): void {
  if (fromQid === toQid) return;
  const target = state.targets.find((t) => t.id === targetId);
  if (!target) return;
  const current = effectivePriority(target);
  const fromIdx = current.indexOf(fromQid);
  if (fromIdx < 0) return;
  const [moving] = current.splice(fromIdx, 1);
  let toIdx = current.indexOf(toQid);
  if (toIdx < 0) {
    current.splice(fromIdx, 0, moving);
    return;
  }
  if (!above) toIdx += 1;
  current.splice(toIdx, 0, moving);
  target.priority = current;
}
